import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Paperclip, X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, errorMessage } from '../../lib/api'
import { useEmployee } from '../../lib/hr'
import { useHolidays, type ApiBalance, type TimePerson } from '../../lib/time'
import { can, canAny, type User as AppUser } from '../../lib/types'
import { useFocusTrap } from '../../lib/useFocusTrap'
import { skipsHolidays, workingDays } from './calendarDays'
import { formatLeaveDays } from '../../lib/leaveDays'
import { addDays, today } from './dates'
import {
  appointedPeerClashes,
  APPROVAL_STAGES,
  awaitingStage,
  HALF_DAY_TYPES,
  LEAVE_BALANCE_OF,
  peerOverlaps,
  peerWindowParams,
  ownAwaitingLeaveParams,
  ownLeaveWindowParams,
  selfOverlaps,
  pickableLeaveTypes,
  rlDisabledReason,
  stageStatus,
  unspentDays,
  type LeaveApplication,
} from './timeData'
import { useMe } from '../../lib/queries'
import { useRoster } from './ui'
import { approvalHandsOver, handoverRightKeys, repChoices, type LeaveHandover } from './leaveHandover'
import { grantTarget } from './leaveTeam'
import { stageSigners } from '../../lib/trailEntry'
import { LeaveBalanceStrip } from './LeaveBalanceStrip'
import LeaveTrail, { useLeaveEvents } from './LeaveTrail'
import {
  Button,
  Checkbox,
  dialogPanelClass,
  IconButton,
  ModalBackdrop,
  Notice,
  Select,
  StatusPill,
  TextArea,
  TextInput,
} from '../../components/ui'

/*
 * Patched rather than re-declared, so a hand-copied duplicate of `dialogPanelClass` cannot drift
 * from the one in `components/ui`.
 */
const widePanelClass = dialogPanelClass.replace('sm:max-w-lg', 'sm:max-w-4xl')
if (import.meta.env.DEV && widePanelClass === dialogPanelClass) {
  console.warn('[LeaveDialogs] dialogPanelClass lost sm:max-w-lg; Overlay wide is now a no-op')
}

export function Overlay({
  children,
  onClose,
  labelledBy,
  wide = false,
}: {
  children: React.ReactNode
  onClose: () => void
  labelledBy: string
  wide?: boolean
}) {
  const trapRef = useFocusTrap<HTMLDivElement>()
  return (
    <ModalBackdrop ref={trapRef} onClose={onClose} aria-labelledby={labelledBy}>
      <div className={wide ? widePanelClass : dialogPanelClass}>{children}</div>
    </ModalBackdrop>
  )
}

interface NewLeavePayload {
  leave_type: string
  start_date: string
  end_date: string
  half_day: boolean
  reason: string
  stand_in_id?: string
}

/** Calendar days, not working days. */
function defaultStartFor(leaveType: string): string {
  return addDays(today(), leaveType === 'AL - Annual Leave' ? 10 : 1)
}

export function leaveSubmitDisabled({
  invalidRange,
  overBalance,
  isPending,
  overOneDay = false,
  missingReason = false,
  selfOverlap = false,
}: {
  invalidRange: boolean
  overBalance: boolean
  isPending: boolean
  /** The form never builds one (`leaveEndFor` pins the To date), so this is the backstop. */
  overOneDay?: boolean
  missingReason?: boolean
  /** The server 409s any date shared with the applicant's own live request. */
  selfOverlap?: boolean
}): boolean {
  return invalidRange || overBalance || isPending || overOneDay || missingReason || selfOverlap
}

/** The server refuses a longer one; this form never offers it. */
const ONE_DAY_LEAVE_TYPES: readonly string[] = ['PSL - Paid Sick Leave']

export function leaveIsOneDay(leaveType: string): boolean {
  return ONE_DAY_LEAVE_TYPES.includes(leaveType)
}

export function leaveEndFor(leaveType: string, start: string, end: string): string {
  return leaveIsOneDay(leaveType) ? start : end
}

export function NewLeaveDialog({
  balance,
  mayReadPeers,
  employeeId,
  workType,
  people,
  onClose,
  onCreate,
  isPending = false,
}: {
  balance: ApiBalance | undefined
  /** Without staff the server refuses other people's leave, so the peer warning is not asked for. */
  mayReadPeers: boolean
  employeeId: string
  /** Without it this form would quote the Full-Time figure to people who are not on that branch. */
  workType: string | undefined
  /**
   * The requester cannot name themselves -- the server's CHECK constraint would refuse it -- so they
   * are filtered out here rather than left in as a 422 waiting to happen.
   */
  people: TimePerson[]
  onClose: () => void
  onCreate: (payload: NewLeavePayload) => void
  isPending?: boolean
}) {
  const { t, i18n } = useTranslation()
  const isFullTime = skipsHolidays(workType)
  const options = useMemo(() => pickableLeaveTypes(isFullTime, balance), [isFullTime, balance])
  const [type, setType] = useState<string>(() => options[0] ?? 'AL - Annual Leave')
  const [start, setStart] = useState(() => defaultStartFor(options[0] ?? 'AL - Annual Leave'))
  const [endPicked, setEnd] = useState(() => defaultStartFor(options[0] ?? 'AL - Annual Leave'))
  const [halfDay, setHalfDay] = useState(false)
  const [reason, setReason] = useState('')
  const oneDay = leaveIsOneDay(type)
  const end = leaveEndFor(type, start, endPicked)
  const [standIn, setStandIn] = useState('')
  const standInOptions = useMemo(
    () =>
      repChoices(
        people.filter((p) => p.id !== employeeId),
        people.find((p) => p.id === employeeId)?.work_type,
      ),
    [people, employeeId],
  )
  const invalidRange = end < start
  useHolidays(start, end)
  const defaultPeerQuery = useQuery({
    queryKey: ['time', 'leave', 'stand-in-default', start, end],
    queryFn: async () =>
      (
        await api.get<{ person: { id: string; name: string } | null }>(
          '/api/time/leave/stand-in-default',
          { params: { start_date: start, end_date: end } },
        )
      ).data,
    enabled: !invalidRange,
  })
  const defaultPeer = defaultPeerQuery.data?.person ?? null
  const handoverPreview = useLeaveHandoverPreview(standIn || defaultPeer?.id || '')
  const standInDefaultLabel = !defaultPeerQuery.isSuccess
    ? t('leave.standInDefault')
    : defaultPeer
      ? t('leave.standInDefaultNamed', { name: defaultPeer.name })
      : t('leave.standInDefaultNone')

  // A half day is offered for five types only, and a type change has to take the flag with it — a
  // checkbox that stays ticked after it stops being rendered would send `half_day` on a type the
  // server refuses outright.
  const canHalfDay = HALF_DAY_TYPES.includes(type)
  const singleDay = start === end
  const days = singleDay && halfDay ? 0.5 : workingDays(start, end, isFullTime)
  const balanceKey = LEAVE_BALANCE_OF[type]
  const available = balanceKey && balance ? Number(balance[balanceKey]) : null
  // They are not off the balance yet, so they have to come off the figure this form checks against —
  // three affordable requests must not together overdraw an entitlement.
  const ownAwaiting = useQuery({
    queryKey: ['time', 'leave', 'own-awaiting'],
    queryFn: async () =>
      (await api.get<LeaveApplication[]>('/api/time/leave', { params: ownAwaitingLeaveParams() })).data,
  })
  const pending = useMemo(
    () => unspentDays(ownAwaiting.data ?? [], employeeId, type),
    [ownAwaiting.data, employeeId, type],
  )
  const remaining = available === null ? null : available - pending
  const peerParams = peerWindowParams(start, end, mayReadPeers)
  const peerWindow = useQuery({
    queryKey: ['time', 'leave', 'peer-window', start, end],
    queryFn: async () => (await api.get<LeaveApplication[]>('/api/time/leave', { params: peerParams })).data,
    enabled: peerParams !== null && !invalidRange,
  })
  const overlaps = useMemo(
    () => peerOverlaps(peerWindow.data ?? [], employeeId, start, end),
    [peerWindow.data, employeeId, start, end],
  )
  // Two requests covering one date both debit the balance, and `_refuse_if_overdrawn` cannot see it
  // when they are different leave types.
  const ownWindow = useQuery({
    queryKey: ['time', 'leave', 'own-window', start, end],
    queryFn: async () =>
      (await api.get<LeaveApplication[]>('/api/time/leave', { params: ownLeaveWindowParams(start, end) })).data,
    enabled: !invalidRange,
  })
  const ownOverlaps = useMemo(
    () => selfOverlaps(ownWindow.data ?? [], employeeId, start, end),
    [ownWindow.data, employeeId, start, end],
  )
  // Who names the APPLICANT as their peer is not readable without `hr.view`; that half is the
  // server's refusal, shown by the caller's error toast.
  const myRecord = useEmployee(employeeId)
  const myPeerId = myRecord.data?.peer_id ?? null
  const peerClash = useMemo(
    () =>
      appointedPeerClashes(peerWindow.data ?? [], myPeerId ? [myPeerId] : [], type, start, end)[0] ??
      null,
    [peerWindow.data, myPeerId, type, start, end],
  )
  const peerClashName = peerClash
    ? (people.find((p) => p.id === peerClash.employee_id)?.name ?? '')
    : ''
  // The server refuses this with a 422; the button must not offer the click.
  const overBalance = remaining !== null && remaining - days < 0
  const [tried, setTried] = useState(false)
  const reasonRef = useRef<HTMLTextAreaElement>(null)
  const blocked = leaveSubmitDisabled({
    invalidRange,
    overBalance,
    isPending,
    overOneDay: oneDay && start !== end,
    selfOverlap: ownOverlaps.length > 0,
  })

  return (
    <Overlay labelledBy="leave-new-title" onClose={onClose}>
      <div className="flex items-start justify-between gap-3">
        <h2 id="leave-new-title" className="text-base font-semibold text-slate-800 dark:text-slate-100">
          {t('leave.newRequest')}
        </h2>
        <IconButton label={t('action.cancel')} icon={<X className="h-4 w-4" />} onClick={onClose} />
      </div>

      <Select
        label={t('leave.type')}
        required
        value={type}
        onChange={(e) => {
          setType(e.target.value)
          if (!HALF_DAY_TYPES.includes(e.target.value)) setHalfDay(false)
          setStart(defaultStartFor(e.target.value))
          setEnd(defaultStartFor(e.target.value))
        }}
      >
        {options.map((option) => {
          const disabledReason =
            option === 'RL - Roster Leave' ? rlDisabledReason(balance) : null
          return (
            <option key={option} value={option} disabled={!!disabledReason}>
              {disabledReason ? `${option} — ${t(disabledReason)}` : option}
            </option>
          )
        })}
      </Select>

      <div className="grid grid-cols-2 gap-3">
        <TextInput
          label={t('leave.from')}
          required
          type="date"
          value={start}
          onChange={(e) => {
            setStart(e.target.value)
            if (endPicked < e.target.value) setEnd(e.target.value)
          }}
        />
        <TextInput
          label={t('leave.to')}
          required
          type="date"
          value={end}
          min={start}
          disabled={oneDay}
          error={invalidRange ? t('leave.rangeInvalid') : undefined}
          onChange={(e) => setEnd(e.target.value)}
        />
      </div>

      {oneDay && (
        <p data-testid="leave-one-day" className="text-xs text-slate-500 dark:text-slate-400">
          {t('leave.oneDayOnly')}
        </p>
      )}

      {singleDay && canHalfDay && (
        <Checkbox label={t('leave.halfDay')} checked={halfDay} onChange={setHalfDay} />
      )}

      <TextArea
        ref={reasonRef}
        label={t('leave.reason')}
        required
        rows={3}
        value={reason}
        error={tried && !reason.trim() ? t('leave.reasonMissing') : undefined}
        aria-invalid={tried && !reason.trim() ? true : undefined}
        onChange={(e) => setReason(e.target.value)}
      />

      <Select label={t('leave.standIn')} value={standIn} onChange={(e) => setStandIn(e.target.value)}>
        <option value="">{standInDefaultLabel}</option>
        {standInOptions.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </Select>
      {handoverPreview.data && <HandoverNote handover={handoverPreview.data} sentenceKey="leave.handoverForm" />}

      <dl className="space-y-1 rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-900/40">
        <div className="flex justify-between">
          <dt className="text-slate-500 dark:text-slate-400">{t('leave.requestedDays')}</dt>
          <dd className="tabular-nums text-slate-800 dark:text-slate-100">{days}</dd>
        </div>
        {pending > 0 && (
          <div className="flex justify-between">
            <dt className="text-slate-500 dark:text-slate-400">{t('leave.pendingDays')}</dt>
            <dd className="tabular-nums text-slate-800 dark:text-slate-100">{formatLeaveDays(pending, i18n.language)}</dd>
          </div>
        )}
        {remaining !== null && (
          <div className="flex justify-between">
            <dt className="text-slate-500 dark:text-slate-400">{t('leave.afterRequest')}</dt>
            <dd
              className={`tabular-nums ${
                overBalance
                  ? 'rounded bg-red-50 px-1 text-red-800 dark:bg-red-950/30 dark:text-red-200'
                  : 'text-slate-800 dark:text-slate-100'
              }`}
            >
              {formatLeaveDays(remaining, i18n.language)} → {formatLeaveDays(remaining - days, i18n.language)}
            </dd>
          </div>
        )}
      </dl>

      {overBalance && <Warning text={t('leave.overBalance')} />}
      {peerClash && (
        <Warning
          text={t('leave.appointedPeerClash', {
            name: peerClashName,
            from: peerClash.start_date,
            to: peerClash.end_date,
          })}
        />
      )}
      {ownOverlaps.length > 0 && (
        <Warning text={t('leave.selfOverlapCount', { count: ownOverlaps.length })} />
      )}
      {overlaps.length > 0 && (
        <Warning text={t('leave.peerOverlapCount', { count: overlaps.length })} />
      )}

      <p className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
        <Paperclip className="h-3.5 w-3.5" />
        {t('leave.attachmentHint')}
      </p>

      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>{t('action.cancel')}</Button>
        <Button
          variant="primary"
          onClick={() => {
            if (!reason.trim()) {
              setTried(true)
              reasonRef.current?.focus()
              return
            }
            onCreate({
              leave_type: type,
              start_date: start,
              end_date: end,
              half_day: singleDay && canHalfDay && halfDay,
              reason,
              ...(standIn ? { stand_in_id: standIn } : {}),
            })
          }}
          disabled={blocked}
          disabledReason={
            isPending
              ? t('leave.submitting')
              : invalidRange
                ? t('leave.rangeInvalid')
                : overBalance
                  ? t('leave.overBalance')
                  : ownOverlaps.length > 0
                    ? t('leave.selfOverlapCount', { count: ownOverlaps.length })
                    : undefined
          }
        >
          {t('leave.submit')}
        </Button>
      </div>
    </Overlay>
  )
}

function Warning({ text }: { text: string }) {
  return (
    <p className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      {text}
    </p>
  )
}

const OVERRIDE_GATES = [
  { value: 'leave_block', label: 'Mandatory annual-leave block (PSL / CL)' },
  { value: 'al_notice_period', label: 'Annual-leave notice period (AL)' },
] as const

export interface GrantOverridePayload {
  person_id: string
  reason: string
  /**
   * Sent explicitly rather than relying on the server default, so a grantor cannot issue the wrong
   * permission by omission -- the two are not interchangeable and one does not imply the other.
   */
  gate: string
}

function useRequiredReason() {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [tried, setTried] = useState(false)
  const reasonRef = useRef<HTMLTextAreaElement>(null)
  const reasonError = tried && !reason.trim() ? t('leave.reasonMissing') : undefined
  function whenGiven(act: (reason: string) => void) {
    if (!reason.trim()) {
      setTried(true)
      reasonRef.current?.focus()
      return
    }
    act(reason)
  }
  return { reason, setReason, reasonRef, reasonError, whenGiven }
}

export function GrantOverrideDialog({
  people,
  onClose,
  onGrant,
}: {
  people: TimePerson[]
  onClose: () => void
  onGrant: (payload: GrantOverridePayload) => void
}) {
  const { t } = useTranslation()
  const [picked, setPicked] = useState('')
  const personId = grantTarget(people, picked)
  const [gate, setGate] = useState<string>(OVERRIDE_GATES[0].value)
  const { reason, setReason, reasonRef, reasonError, whenGiven } = useRequiredReason()

  return (
    <Overlay labelledBy="leave-grant-title" onClose={onClose}>
      <div className="flex items-start justify-between gap-3">
        <h2 id="leave-grant-title" className="text-base font-semibold text-slate-800 dark:text-slate-100">
          Grant a leave-gate override
        </h2>
        <IconButton label={t('action.cancel')} icon={<X className="h-4 w-4" />} onClick={onClose} />
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Lets the named person past ONE of the two leave gates, once. It is not a
        blanket exemption: every balance and limit check still applies, and the
        gate you pick below is the only one lifted. Granting it to yourself is
        allowed -- it is recorded and shown the same way as any other grant.
      </p>

      <Select label="Person" required value={personId} onChange={(e) => setPicked(e.target.value)}>
        {people.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </Select>

      <Select label="Gate" required value={gate} onChange={(e) => setGate(e.target.value)}>
        {OVERRIDE_GATES.map((g) => (
          <option key={g.value} value={g.value}>
            {g.label}
          </option>
        ))}
      </Select>

      <TextArea
        ref={reasonRef}
        label="Reason"
        required
        rows={2}
        value={reason}
        error={reasonError}
        onChange={(e) => setReason(e.target.value)}
      />

      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>{t('action.cancel')}</Button>
        <Button
          variant="primary"
          onClick={() => whenGiven((given) => onGrant({ person_id: personId, reason: given, gate }))}
          disabled={!personId}
          disabledReason={!personId ? 'Pick a person first' : undefined}
        >
          Grant
        </Button>
      </div>
    </Overlay>
  )
}

export function OverrideRuleDialog({
  message,
  isPending,
  onClose,
  onOverride,
}: {
  message: string
  isPending: boolean
  onClose: () => void
  onOverride: (reason: string) => void
}) {
  const { t } = useTranslation()
  const { reason, setReason, reasonRef, reasonError, whenGiven } = useRequiredReason()

  return (
    <Overlay labelledBy="leave-override-title" onClose={onClose}>
      <div className="flex items-start justify-between gap-3">
        <h2 id="leave-override-title" className="text-base font-semibold text-slate-800 dark:text-slate-100">
          {t('timeadmin.overrideTitle')}
        </h2>
        <IconButton label={t('action.cancel')} icon={<X className="h-4 w-4" />} onClick={onClose} />
      </div>

      {message && (
        <Notice icon={<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}>{message}</Notice>
      )}
      <p className="text-xs text-slate-500 dark:text-slate-400">{t('timeadmin.overrideHint')}</p>

      <TextArea
        ref={reasonRef}
        label={t('timeadmin.overrideReason')}
        required
        rows={2}
        value={reason}
        maxLength={500}
        error={reasonError}
        onChange={(e) => setReason(e.target.value)}
      />

      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>{t('action.cancel')}</Button>
        <Button
          variant="primary"
          onClick={() => whenGiven(onOverride)}
          disabled={isPending}
          disabledReason={isPending ? t('leave.submitting') : undefined}
        >
          {t('timeadmin.overrideConfirm')}
        </Button>
      </div>
    </Overlay>
  )
}

/** Pure and exported so it can be pinned without a DOM (vitest runs in node). */
export function canDecideLeave(
  app: LeaveApplication,
  isOwn: boolean,
  superviseeIds: Set<string>,
  user: AppUser | undefined,
): boolean {
  if (isOwn) return false
  return APPROVAL_STAGES.some((stage) => {
    if (!awaitingStage(app, stage)) return false
    if (stage === 'supervisor') return superviseeIds.has(app.employee_id) || app.on_behalf_of !== null
    if (stage === 'management') return canAny(user, ['requests.approve'])
    if (stage === 'finance') return can(user, 'signoff.cfo')
    return false
  })
}

/**
 * The three approval lines are always shown, including the ones that do not apply, so it is
 * obvious who has not signed yet rather than who is missing from the list.
 */
function useLeaveHandoverPreview(standInId: string) {
  return useQuery({
    queryKey: ['time', 'leave', 'handover-preview', standInId],
    queryFn: async () =>
      (await api.get<LeaveHandover>('/api/time/leave/handover-preview', { params: { stand_in_id: standInId } })).data,
    enabled: standInId !== '',
  })
}

function useLeaveHandover(leaveId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['time', 'leave', leaveId, 'handover'],
    queryFn: async () => (await api.get<LeaveHandover>(`/api/time/leave/${leaveId}/handover`)).data,
    enabled,
  })
}

function HandoverNote({ handover, sentenceKey }: { handover: LeaveHandover; sentenceKey: string }) {
  const { t } = useTranslation()
  const rights = handoverRightKeys(handover)
  if (!handover.refused_not_full_time && rights.length === 0) return null
  return (
    <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
      {handover.refused_not_full_time
        ? t('leave.handoverRefused', { name: handover.stand_in_name })
        : t(sentenceKey, { name: handover.stand_in_name, rights: rights.map((key) => t(key)).join(', ') })}
    </p>
  )
}

export function LeaveDetailDialog({
  app,
  personName,
  isOwn,
  onClose,
  onDecide,
  onCancel,
}: {
  app: LeaveApplication
  personName: string
  isOwn: boolean
  onClose: () => void
  onDecide: (verdict: 'Approved' | 'Rejected', comment: string) => void
  onCancel: () => void
}) {
  const { t, i18n } = useTranslation()
  const [comment, setComment] = useState('')
  const [rejectTried, setRejectTried] = useState(false)
  const commentRef = useRef<HTMLTextAreaElement>(null)

  // `useMe`/`useRoster` are the same react-query hooks `Leave.tsx` already calls to build its own
  // `superviseeIds` -- same query keys, so this reads the cache Leave.tsx already primed rather than
  // issuing a second fetch.
  const { data: user } = useMe()
  const { supervisees } = useRoster()
  const superviseeIds = useMemo(() => new Set(supervisees.map((p) => p.id)), [supervisees])
  const canDecide = canDecideLeave(app, isOwn, superviseeIds, user)
  const cancelling = app.status === 'Cancel Pending'
  const { data: events } = useLeaveEvents(app.id)
  const handoverQ = useLeaveHandover(app.id, approvalHandsOver(canDecide, app.status))
  const signers = stageSigners(events ?? [], (stage) =>
    stageStatus(app, stage as (typeof APPROVAL_STAGES)[number]),
  )

  return (
    <Overlay labelledBy="leave-person-title" onClose={onClose}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="leave-person-title" className="text-base font-semibold text-slate-800 dark:text-slate-100">
            {personName}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{app.leave_type}</p>
          {/*
             Present only when the caller sees this row because they are standing in -- `on_behalf_of` is
             `null` for a row they own.
          */}
          {app.on_behalf_of && (
            <p className="mt-1 inline-block rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
              {t('leave.onBehalfOf', { name: app.on_behalf_of.name })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={app.status} />
          <IconButton label={t('action.cancel')} icon={<X className="h-4 w-4" />} onClick={onClose} />
        </div>
      </div>

      <dl className="space-y-1 text-sm">
        <Row label={t('leave.dates')} value={`${app.start_date} → ${app.end_date}`} />
        <Row
          label={t('leave.requestedDays')}
          value={`${formatLeaveDays(app.days, i18n.language)}${app.half_day ? ` (${t('leave.halfDay')})` : ''}`}
        />
        <Row label={t('leave.requestedOn')} value={app.requested_date ?? '—'} />
        <Row label={t('leave.reason')} value={app.reason || '—'} />
        {app.attachments > 0 && (
          <Row label={t('leave.attachments')} value={String(app.attachments)} />
        )}
      </dl>
      {canDecide && <LeaveBalanceStrip employeeId={app.employee_id} />}
      {handoverQ.data && <HandoverNote handover={handoverQ.data} sentenceKey="leave.handoverApprove" />}
      {handoverQ.isError && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-300">
          {t('leave.handoverLoadFailed')} {errorMessage(handoverQ.error)}
        </p>
      )}

      <div className="space-y-1 rounded-lg bg-slate-50 p-3 dark:bg-slate-900/40">
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {t('leave.approvals')}
        </p>
        {APPROVAL_STAGES.map((stage) => (
          <div key={stage} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-slate-600 dark:text-slate-300">{t(`leave.stage.${stage}`)}</span>
            <span className="flex items-center gap-2">
              {signers[stage] && (
                <span className="text-xs text-slate-500 dark:text-slate-400">{signers[stage]}</span>
              )}
              <StatusPill status={stageStatus(app, stage)} />
            </span>
          </div>
        ))}
        <p className="pt-1 text-xs text-slate-500 dark:text-slate-400">{t('leave.finalRule')}</p>
      </div>

      {/*
         A rejection is never anonymous: the applicant gets the name and the reason together, in red,
         rather than an unattributed note.
      */}
      {app.status === 'Rejected' && (app.rejected_by || app.comment || app.system_reason) ? (
        <div className="space-y-1 rounded-lg bg-red-50 p-2 text-xs text-red-800 dark:bg-red-950/30 dark:text-red-200">
          {app.rejected_by && (
            <p className="font-medium">
              {t('leave.rejectedBy')} {app.rejected_by}
            </p>
          )}
          {app.comment && <p>{app.comment}</p>}
          {app.system_reason && <p className="italic opacity-80">{app.system_reason}</p>}
        </div>
      ) : (
        (app.comment || app.system_reason) && (
          <p className="rounded-lg border border-slate-200 p-2 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300">
            {app.comment}
            {app.system_reason && (
              <span className="block italic text-slate-500 dark:text-slate-400">
                {app.system_reason}
              </span>
            )}
          </p>
        )
      )}

      <LeaveTrail leaveId={app.id} />

      {canDecide && (
        <TextArea
          ref={commentRef}
          label={t('leave.comment')}
          rows={2}
          value={comment}
          placeholder={t('leave.rejectionHint')}
          error={rejectTried && !comment.trim() ? t('leave.reasonRequired') : undefined}
          onChange={(e) => setComment(e.target.value)}
        />
      )}

      <div className="flex flex-wrap justify-end gap-2">
        {isOwn && app.status === 'Pending' && <Button onClick={onCancel}>{t('leave.withdraw')}</Button>}
        {isOwn && app.status === 'Approved' && <Button onClick={onCancel}>{t('leave.requestCancel')}</Button>}
        {isOwn && cancelling && <Button onClick={onCancel}>{t('leave.stopCancel')}</Button>}

        {canDecide && (
          <>
            <Button
              variant="danger"
              onClick={() => {
                if (!comment.trim()) {
                  setRejectTried(true)
                  commentRef.current?.focus()
                  return
                }
                onDecide('Rejected', comment)
              }}
            >
              {cancelling ? t('leave.rejectCancel') : t('leave.reject')}
            </Button>
            <Button variant="approve" onClick={() => onDecide('Approved', comment)}>
              {cancelling ? t('leave.approveCancel') : t('leave.approve')}
            </Button>
          </>
        )}
      </div>
    </Overlay>
  )
}

const BALANCE_GUIDE: Array<[heading: string, body: string]> = [
  ['timeadmin.guideEditing', 'timeadmin.leaveHint'],
  ['timeadmin.guideVersioning', 'timeadmin.balanceVersioned'],
  ['timeadmin.guideLedger', 'leave.ledgerHint'],
]

export function BalanceGuideDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <Overlay labelledBy="leave-guide-title" onClose={onClose}>
      <div className="flex items-start justify-between gap-3">
        <h2 id="leave-guide-title" className="text-base font-semibold text-slate-800 dark:text-slate-100">
          {t('timeadmin.balanceGuide')}
        </h2>
        <IconButton label={t('action.close')} icon={<X className="h-4 w-4" />} onClick={onClose} />
      </div>

      <dl className="space-y-3 text-sm">
        {BALANCE_GUIDE.map(([heading, body]) => (
          <div key={heading}>
            <dt className="font-medium text-slate-800 dark:text-slate-100">{t(heading)}</dt>
            <dd className="mt-0.5 text-slate-600 dark:text-slate-300">{t(body)}</dd>
          </div>
        ))}
      </dl>

      <div className="flex justify-end">
        <Button onClick={onClose}>{t('action.close')}</Button>
      </div>
    </Overlay>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-right text-slate-700 dark:text-slate-200">{value}</dd>
    </div>
  )
}
